import { useState } from 'react';
import './dashShared.css';

import GAMain from './GAMain';
import MarketingMain from './MarketingMain';
import RnDMain from './RnDMain';
import CSMain from './CSMain';
import DevOpsMain from './DevOpsMain';
import PMMain from './PMMain';
import APACMain from './APACMain';
import NAMMain from './NAMMain';
import SOLMain from './SOLMain';
import GlobalMain from './GlobalMain';
import { DevOverlay } from '../components/DevOverlay/DevOverlay';

// Global is a synthetic tab, not gated by its own access key — reaching
// DepartmentMain at all already means the user has at least one of the
// nine department permissions below, and the Global Dashboard's own API
// filters its data down to just the departments that user can see (see
// global_views/dashboard.py), so showing the tab itself needs no separate
// permission check.
const GLOBAL_TAB = { id: 'global', label: 'Global', dot: '#0f172a' };

const DEPT_TABS = [
    { id: 'devops', label: 'Dev Ops', dot: '#2563eb' },
    { id: 'cs', label: 'CS', dot: '#059669' },
    { id: 'pm', label: 'Product Management', dot: '#db2777' },
    { id: 'rnd', label: 'R&D', dot: '#6d28d9' },
    { id: 'nam', label: 'Sales NAM', dot: '#65a30d' },
    { id: 'apac', label: 'Sales APAC', dot: '#0891b2' },
    { id: 'sol', label: 'Sales Solution', dot: '#9333ea' },
    { id: 'marketing', label: 'Marketing', dot: '#d97706' },
    { id: 'ga', label: 'G&A', dot: '#ef0000' },
];

function DepartmentMain({ user, onDataLoaded }) {
    const access = user?.isSuperuser ? DEPT_TABS.map(t => t.id) : (user?.access || []);
    const visibleDeptTabs = DEPT_TABS.filter(t => access.includes(t.id));
    const visibleTabs = visibleDeptTabs.length ? [GLOBAL_TAB, ...visibleDeptTabs] : [];
    const [dept, setDept] = useState(GLOBAL_TAB.id);
    const activeDept = visibleTabs.some(t => t.id === dept) ? dept : visibleTabs[0]?.id;

    if (!visibleTabs.length) {
        return (
            <section className="settings-page">
                <article className="settings-panel">
                    <span className="settings-kicker">Department Dashboard</span>
                    <h2>Access required</h2>
                    <p className="settings-panel-description">You do not have access to any department dashboard. Please contact the Application Administrator.</p>
                </article>
            </section>
        );
    }

    return (
        <div className="dash-shell">
            <DevOverlay name="DepartmentTabNavigation">
                <div className="dash-subnav">
                    <div className="dash-tabs">
                        {visibleTabs.map(t => (
                            <button
                                key={t.id}
                                className={`dash-tab${activeDept === t.id ? ' active' : ''}`}
                                onClick={() => setDept(t.id)}
                            >
                                <span
                                    className="dash-tab-dot"
                                    style={{ background: activeDept === t.id ? t.dot : 'transparent', border: `2px solid ${t.dot}` }}
                                />
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </DevOverlay>

            {/* Every dashboard below renders its own dash-shell/dash-subnav
                internally (sticky at top: var(--sticky-offset, var(--nav-h))
                — see dashShared.css) — nesting that directly under the tab
                bar above (also sticky, at the same default offset) would
                overlap. Setting --sticky-offset here, for this subtree only,
                makes every nested sticky element (dash-subnav, filter bars,
                DevOverlay) compute its offset relative to the bottom of the
                tab bar above instead, with no changes needed in any of the
                nine dashboard components themselves.
                Deliberately NOT named --nav-h: redeclaring --nav-h here as
                `calc(var(--nav-h) + ...)` looks like the obvious way to
                "bump the current value," but a custom property that
                references itself in its own declaration (even through
                var()'s own fallback) is a genuine CSS dependency cycle —
                confirmed directly via computed-style inspection, not just
                theory: browsers resolve it as invalid-at-computed-value-time,
                so --nav-h came back empty on this element, and every nested
                sticky element silently fell back to the raw default instead
                of the intended stacked offset. That was the actual cause of
                the department tab row disappearing/getting overlapped on
                scroll. Using a distinct property name sidesteps the cycle
                entirely, since it never references itself. */}
            <div style={{ '--sticky-offset': 'calc(var(--nav-h, 52px) + var(--subnav-h, 42px))' }}>
                {activeDept === 'global' && <GlobalMain onDataLoaded={onDataLoaded} />}
                {activeDept === 'ga' && <GAMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'marketing' && <MarketingMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'rnd' && <RnDMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'cs' && <CSMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'devops' && <DevOpsMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'pm' && <PMMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'apac' && <APACMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'nam' && <NAMMain user={user} onDataLoaded={onDataLoaded} />}
                {activeDept === 'sol' && <SOLMain user={user} onDataLoaded={onDataLoaded} />}
            </div>
        </div>
    );
}

export default DepartmentMain;
